/**
 * 首页图表（2026-10-08，堆叠改版 2026-10-09）—— 纯 SVG/DOM，零图表库依赖。
 *
 * ## 为什么不用 recharts / chart.js
 *
 * 本项目依赖里没有任何图表库（见 package.json），首页是第一个需要图表的地方。
 * 引入图表库要付出：+80~150KB 客户端体积（本项目已有 ComfyUI/编辑器的重量）、
 * 一套与项目现有 CSS 变量体系平行的配色机制、以及 SSR 下的 hydration 处理。
 * 而看板只需要**两种最简图形**：堆叠柱状趋势、进度条（进度条在 production-cards）。
 * 所以手写。
 *
 * 好处是颜色/字号/圆角全部走项目令牌（`--chart-1`、`--text-secondary`…），
 * 与门禁 `home-dashboard-gate.test.mjs` 的判据一致；图表库反而会引入私有主题层。
 *
 * ## 🔴 2026-10-09：环形占比图与横向排行图**被删除**，不是被弃用
 *
 * 两张图都还在别处「能用」，但它们回答的问题在本页**不可行动**：
 *   · 环形占比（14 个项目的阶段分布）：14 个项目切 4~5 段，每段 1~8 个，
 *     占比差异读不出行动；它的信息已并入漏斗卡的阶段图例（同一处、同一眼）；
 *   · 横向排行（分镜数 Top N）：**存量**排名，单调只增、每次打开都一样，
 *     看到第一名叫「天地崩 1098」也不知道下一步做什么。
 * 留着它们等于留两张「看着热闹但推不动事」的图 —— 首页的每一格都要能指向一个动作。
 * 接替它们的是 `production-cards.tsx` 的漏斗卡与待处理卡。
 *
 * ## 无障碍
 *
 * 图形一律 `role="img"` + `<title>`，并额外给一份 `sr-only` 文本表格 ——
 * 纯图形对屏幕阅读器是空的，这是「有图」不等于「可用」。
 */

"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";

import { F } from "@/components/studio/ui";
import type { HomeStackedTrend } from "@/lib/home/types";

import { EmptyIcon, HomeEmpty } from "./home-primitives";

// ── 工具 ────────────────────────────────────────────────────────

/** 大数字缩写：12345 → 12.3k。避免坐标轴被长数字撑开。 */
function abbr(n: number): string {
  if (Math.abs(n) < 1000) return String(n);
  if (Math.abs(n) < 1_000_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
}

/** 主题色 token → 具体色值。图表要用 fill/stroke，只能给具体色。 */
const TONE_VAR: Record<string, string> = {
  "chart-1": "var(--chart-1)",
  "chart-2": "var(--chart-2)",
  "chart-3": "var(--chart-3)",
  "chart-4": "var(--chart-4)",
  "chart-5": "var(--chart-5)",
  neutral: "var(--border-hover)",
};

export function toneColor(tone: string): string {
  return TONE_VAR[tone] ?? "var(--chart-1)";
}

/** 视觉隐藏但屏幕阅读器可读的文本。 */
function SrOnly({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}

// ── 趋势（按类型堆叠） ──────────────────────────────────────────

/**
 * 堆叠柱状趋势图。
 *
 * 🔴 为什么是堆叠而不是单条总量（2026-10-09）：
 * 单条总量只回答「今天产了多少」，回答不了**产能在哪一环** ——
 * 而这一环决定该去补数据还是补算力。「视频占比突然归零」在总量曲线上
 * 只表现为矮一截，在堆叠图上是一条颜色整段消失。
 *
 * 🔴 四个必须处理的细节，否则图表会「看起来对但骗人」：
 * ① **Y 轴从 0 起**：不从 0 起会放大微小波动，趋势失真；
 * ② **零值柱画基线**：值为 0 时画 1px 底线，否则用户以为数据缺失；
 * ③ **日期标签抽稀**：14 天全标会糊成一片，按宽度取 4~5 个；
 * ④ **堆叠高度按「当日合计」的比例**：每段高度 = 段值 / 当日合计最大值，
 *    直接用段值除以自身合计会让每根柱子都满格 —— 总量差异被抹平。
 */
export function TrendChart({
  trend,
  label,
  unit,
  emptyText,
  seriesLabelOf,
}: {
  trend: HomeStackedTrend;
  label: string;
  unit?: string;
  /** 完全无数据点（连窗口都取不到）时的文案。🔴 有数据但全零走 charts.allZero，
      不走这里 —— 那是另一种语义，两者混用会让用户以为图挂了。 */
  emptyText: string;
  /** 数据层的 series.labelKey 带 `home.` 前缀，必须由调用方用根翻译器解析后传入 */
  seriesLabelOf: (key: string) => string;
}) {
  const points = trend.points;
  // 每根柱子的总量：步长以它为准，保证柱子之间的**高低差**仍然可读
  const dayTotals = points.map((p) => p.values.reduce((s, v) => s + v, 0));
  const max = Math.max(1, ...dayTotals);
  const total = dayTotals.reduce((s, v) => s + v, 0);
  // 🔴 本组件自有的字面量文案走 **home 命名空间的相对键**（`charts.xxx`）。
  // 数据层给的 labelKey/unitKey 则由调用方用根翻译器解析后当 props 传进来。
  const t = useTranslations("home");

  // 🔴 两种空态必须分开：
  // ① 没有数据点 → emptyText；② 有数据点但全为 0 → allZeroText。
  // 首版把 ② 画成「14 根 1px 基线 + 空坐标轴」——用户看到的是一大块空白，
  // 分不清「没有产出」还是「图挂了」。
  //
  // 🔴 窗口天数由**数据**给出（points.length）：写死「近 14 天」的话，
  // 一旦 days 改成 7 或 30，文案还在说 14 —— 界面平静地撒谎，没有任何报错。
  const fill = { count: points.length || 0 };
  if (points.length === 0) {
    return <HomeEmpty icon={<EmptyIcon />} title={emptyText} compact />;
  }
  if (total === 0) {
    return <HomeEmpty icon={<EmptyIcon />} title={t("charts.allZero", fill)} compact />;
  }

  // 标签抽稀：每 labelEvery 个点标一次，末点必标（用户最关心"今天"）
  const step = Math.max(1, Math.ceil(points.length / 5));

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      {/* 汇总行：只给「窗口内合计」一个数。 */}
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl leading-none font-semibold tabular-nums text-[var(--text-primary)]">
          {abbr(total)}
        </span>
        <span className={`${F.deco}`}>{unit}</span>
        <span className={`${F.deco} ml-auto`}>{t("charts.windowDays", fill)}</span>
      </div>

      {/* 🔴 图例带每条系列的窗口合计：堆叠图不标量，读者无法判断某一色块是 3 还是 300。
          合计也解释了「为什么这根柱子高」——高的是哪一类一眼可见。 */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        {trend.series.map((s) => (
          <span key={s.id} className="flex items-center gap-1">
            <span
              aria-hidden
              className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
              style={{ background: toneColor(s.tone) }}
            />
            <span className={F.deco}>{seriesLabelOf(s.labelKey)}</span>
            <span className="text-[10px] leading-none tabular-nums text-[var(--text-muted)]">
              {abbr(s.total)}
            </span>
          </span>
        ))}
      </div>

      {/* 🔴 图区是**高度吸收器**（2026-10-09 纵向对齐）：
          三张卡正文一律顶对齐，"谁吃掉多余高度"必须落在能长高的内容上 ——
          本图长高只会更易读，而列表长高会拉开行距、留白长高就是空洞。
          所以刻度槽与绘图区都写成 `min-h-[96px]` + 靠父级 flex-1 拉伸，
          不再写死 112px（写死就等于把"多余高度"推回给卡片自己留白）。 */}
      <div className="flex min-h-[96px] min-w-0 flex-1 gap-2.5">
        {/* 刻度槽：与图区等高，两端对齐 */}
        <div className="relative w-[26px] shrink-0" aria-hidden>
          <span className="absolute right-0 top-0 text-[10px] leading-none tabular-nums text-[var(--text-muted)]">
            {abbr(max)}
          </span>
          <span className="absolute bottom-0 right-0 text-[10px] leading-none tabular-nums text-[var(--text-muted)]">
            0
          </span>
        </div>

        {/* 🔴 无障碍名挂在**图区容器**上（role="img" + aria-label）。
            旧版是 SVG 里的 `<title>`：`<title>` 只在 SVG 命名空间里生效，
            而本图是 DOM 条形（div + 百分比高度），塞 `<title>` 会被当成普通元素
            渲染成可见文字。role="img" + aria-label 是 DOM 图形的等价物，
            且下方还有一份完整的 sr-only 数表兜底。 */}
        <div
          className="relative min-w-0 flex-1"
          role="img"
          aria-label={`${label} · ${t("charts.windowDays", fill)}`}
        >
          {/* 网格线：从 0 起是硬要求，不从 0 起会放大微小波动。
              只留 2 条（顶=峰值、底=零线）—— 中线没有语义，只与柱子的横向节奏打架。 */}
          {[0, 1].map((g) => (
            <div
              key={g}
              className="pointer-events-none absolute inset-x-0 flex items-center"
              style={{ top: `${(1 - g) * 100}%` }}
            >
              <span className="h-px w-full bg-[var(--border-subtle)]" />
            </div>
          ))}

          <div className="absolute inset-0 flex items-end gap-[3px] pt-1">
            {points.map((p, i) => {
              const sum = dayTotals[i];
              const showLabel = i % step === 0 || i === points.length - 1;
              // 每段按「当日合计」的比例给高，再按峰值归一 —— 见组件头 ④
              const segPct = (v: number) => (max > 0 ? (v / max) * 100 : 0);
              return (
                <div key={p.date} className="relative flex h-full min-w-0 flex-1 flex-col justify-end">
                  {/* 零值柱保留 1px 基线：完全为空会被读作「这天没数据」而不是「这天没产出」 */}
                  {sum === 0 ? (
                    <div className="w-full rounded-t-[2px] bg-[var(--border-subtle)]" style={{ height: 1 }} />
                  ) : (
                    // 从底到顶 = series 顺序倒着渲染（flex-col 末端贴底）
                    [...p.values]
                      .map((v, si) => ({ v, si }))
                      .reverse()
                      .map(({ v, si }) =>
                        v <= 0 ? null : (
                          <div
                            key={trend.series[si]?.id ?? si}
                            className="w-full first:rounded-t-[2px]"
                            style={{ height: `${segPct(v)}%`, background: toneColor(trend.series[si]?.tone ?? "chart-1") }}
                          />
                        ),
                      )
                  )}
                  <span
                    className={`absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tabular-nums text-[var(--text-muted)] ${
                      showLabel ? "" : "invisible"
                    }`}
                  >
                    {p.date.slice(5)}
                  </span>
                  {/* hover 提示：堆叠图必须逐段报数，否则鼠标悬停只能看到总量 */}
                  <span
                    className="pointer-events-none absolute inset-x-0 bottom-0 top-0"
                    title={`${p.date} · ${trend.series
                      .map((s, si) => `${seriesLabelOf(s.labelKey)} ${p.values[si] ?? 0}`)
                      .join(" / ")}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <SrOnly>
        {label}:{" "}
        {points
          .map(
            (p) =>
              `${p.date} ${trend.series.map((s, si) => `${seriesLabelOf(s.labelKey)} ${p.values[si] ?? 0}`).join(" ")}`,
          )
          .join(", ")}
      </SrOnly>
    </div>
  );
}
